import React, { FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import { useTheme, Button, Chip, Tooltip, Grid, Typography } from '@rbx/ui';
import { useRobloxAuthentication } from '@rbx/auth';
import TryItOutAuthMode from '@modules/openCloud/swagger/types/TryItOutAuthMode';
import useTryItOut from '../../hooks/useTryItOut';

const AuthModeSelector: FunctionComponent = () => {
  const { translate } = useTranslation();
  const theme = useTheme();
  const { status, login } = useRobloxAuthentication();
  const { authMode, setAuthMode } = useTryItOut();

  // Create cookie chip (with login support if unauthenticated)
  const createCookieChip = (disabled: boolean, ariaLabel: string) => (
    <Chip
      variant={!disabled && authMode === TryItOutAuthMode.COOKIE ? 'filled' : 'outlined'}
      size='medium'
      color='primary'
      label={translate('Label.TryItOutAuthModeCookie')}
      {...(disabled && { disabled })}
      {...(!disabled && { onClick: () => setAuthMode(TryItOutAuthMode.COOKIE) })}
      style={
        !disabled && authMode === TryItOutAuthMode.COOKIE
          ? { color: theme.palette.content.inverse }
          : undefined
      }
      aria-label={ariaLabel}
    />
  );

  // Get cookie chip with login support if needed
  const getCookieChip = () => {
    if (status === 'unauthenticated') {
      return (
        <Tooltip
          title={
            <div style={{ textAlign: 'center' }}>
              <div>{translate('Description.TryItOutLoginRequired')}</div>
              <div style={{ marginTop: '8px' }}>
                <Button
                  variant='contained'
                  color='primaryBrand'
                  size='small'
                  aria-label={translate('Action.TryItOutLogIn')}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    login();
                  }}>
                  {translate('Action.TryItOutLogIn')}
                </Button>
              </div>
            </div>
          }>
          <span>{createCookieChip(true, translate('Description.TryItOutLoginRequired'))}</span>
        </Tooltip>
      );
    }

    return createCookieChip(false, translate('Label.TryItOutAuthModeCookie'));
  };

  return (
    <Grid container alignItems='center' gap={2}>
      <Grid item>
        <Typography variant='h6'>{translate('Heading.TryItOutAuthMode')}</Typography>
      </Grid>

      <Grid item container alignItems='center' width='fit-content' gap={1}>
        {/* Cookie Authentication Chip */}
        {getCookieChip()}

        {/* None Authentication Chip */}
        <Chip
          variant={authMode === TryItOutAuthMode.NONE ? 'filled' : 'outlined'}
          size='medium'
          color='primary'
          label={translate('Label.TryItOutAuthModeNone')}
          onClick={() => setAuthMode(TryItOutAuthMode.NONE)}
          style={
            authMode === TryItOutAuthMode.NONE
              ? { color: theme.palette.content.inverse }
              : undefined
          }
          aria-label={translate('Label.TryItOutAuthModeNone')}
        />
      </Grid>
    </Grid>
  );
};

export default AuthModeSelector;
